import { Box, Group, Stack, Table, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { SectionAddButton } from 'src/cashflows/components/SectionAddButton';
import { FieldWithValue } from 'src/UI/dynamicUI/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useEntityForm } from '../EntityFormProvider';
import { formatValueForField } from './fields/ViewFormField';
import { ProfileFormField } from './ProfileFormField';
import { ProfileFormList } from './ProfileFormList';

const ENTITY_TYPE = 'AuthorityArrangement';

// What tells two arrangements apart at a glance. The rest opens under the row.
const COLUMNS: { field: string; width: string }[] = [
  { field: 'arrangement_type', width: '30%' },
  { field: 'client_role', width: '15%' },
  { field: 'other_party_names', width: '18%' },
  { field: 'status', width: '21%' },
  { field: 'executed_on', width: '13%' },
];

const DETAIL_FIELDS = [
  'replacement_attorney_names',
  'how_attorneys_act',
  'covers_financial_affairs',
  'activation',
  'restrictions_present',
  'restrictions_notes',
  'registered_on',
  'expires_on',
  'registration_reference',
  'evidence_basis',
  'notes',
];

const FIELDS = [...COLUMNS.map(column => column.field), ...DETAIL_FIELDS];
const LIST_FIELDS = ['other_party_names', 'replacement_attorney_names'];

const defaultFields = Object.fromEntries(
  FIELDS.map(field => [field, LIST_FIELDS.includes(field) ? [''] : '']),
);

const GRAY_1_BORDER = '1px solid var(--mantine-color-gray-1)';

export function AuthorityArrangementFields() {
  const { isEditing } = useEntityForm();

  // Sixteen fields per record read as a table, but they are edited one field at a time, so
  // editing stays on the list the rest of the profile uses.
  return isEditing ? <ArrangementEditor /> : <ArrangementTable />;
}

function ArrangementEditor() {
  return (
    <ProfileFormList
      entityType={ENTITY_TYPE}
      addButtonLabel="Add power of attorney"
      defaultFields={defaultFields}
    >
      {index => (
        <Stack gap="lg">
          {FIELDS.map(field => (
            <ProfileFormField
              key={field}
              entityType={ENTITY_TYPE}
              fieldName={field}
              index={index}
            />
          ))}
        </Stack>
      )}
    </ProfileFormList>
  );
}

function ArrangementTable() {
  const { form, entitiesMetadata, setIsEditing } = useEntityForm();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const metadata = entitiesMetadata[ENTITY_TYPE] ?? {};
  const values = form.getValues()[ENTITY_TYPE];
  const arrangements = Array.isArray(values) ? values : [];

  const startAdding = () => {
    form.insertListItem(ENTITY_TYPE, defaultFields);
    setIsEditing(true);
  };

  const label = (field: string) => metadata[field]?.label ?? field;
  const display = (arrangement: FieldWithValue, field: string) =>
    formatValueForField(arrangement[field], metadata[field]);

  return (
    <Box>
      <Group gap="xs" mb="md">
        <Text fw={FONT_WEIGHTS.semibold}>Powers of attorney</Text>
        <Text size="sm" c="gray.6">
          {arrangements.length}
        </Text>
        <Box ml="auto">
          <SectionAddButton action={{ onAdd: startAdding }} ariaLabel="Add power of attorney" />
        </Box>
      </Group>

      {arrangements.length === 0 ? (
        <Text size="sm" c="gray.6">
          None recorded
        </Text>
      ) : (
        <Table
          withRowBorders={false}
          verticalSpacing={0}
          horizontalSpacing="md"
          layout="fixed"
          styles={{ thead: { backgroundColor: 'transparent' } }}
        >
          <Table.Thead>
            <Table.Tr>
              {COLUMNS.map(column => (
                <Table.Th key={column.field} style={{ paddingBottom: 16, width: column.width }}>
                  <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
                    {label(column.field)}
                  </Text>
                </Table.Th>
              ))}
              <Table.Th style={{ paddingBottom: 16, width: '3%' }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {arrangements.map((arrangement, index) => {
              const isOpen = openIndex === index;
              return (
                <ArrangementRow
                  key={index}
                  arrangement={arrangement}
                  isOpen={isOpen}
                  onToggle={() => setOpenIndex(isOpen ? null : index)}
                  label={label}
                  display={display}
                />
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}

type ArrangementRowProps = {
  arrangement: FieldWithValue;
  isOpen: boolean;
  onToggle: () => void;
  label: (field: string) => string;
  display: (arrangement: FieldWithValue, field: string) => string | string[] | undefined;
};

function ArrangementRow({ arrangement, isOpen, onToggle, label, display }: ArrangementRowProps) {
  const populated = DETAIL_FIELDS.filter(field => display(arrangement, field) != null);

  return (
    <>
      <Table.Tr
        style={{ borderTop: GRAY_1_BORDER, cursor: 'pointer' }}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {COLUMNS.map(column => (
          <RowCell key={column.field}>{display(arrangement, column.field)}</RowCell>
        ))}
        <RowCell align="right">
          {isOpen ? (
            <IconChevronDown size={16} color="var(--mantine-color-gray-6)" />
          ) : (
            <IconChevronRight size={16} color="var(--mantine-color-gray-6)" />
          )}
        </RowCell>
      </Table.Tr>
      {isOpen && (
        <Table.Tr>
          <Table.Td colSpan={COLUMNS.length + 1} pt={0} pb="lg">
            <Box bg="gray.0" p="md" style={{ borderRadius: 12 }}>
              <Stack gap="sm">
                {populated.map(field => (
                  <Group key={field} align="start" wrap="nowrap" gap="md">
                    <Text size="sm" c="gray.6" w={200} style={{ flex: 'none' }}>
                      {label(field)}
                    </Text>
                    <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {renderValue(display(arrangement, field))}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Box>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

function RowCell({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <Table.Td style={{ paddingTop: 16, paddingBottom: 20, textAlign: align }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
        {renderValue(children)}
      </Text>
    </Table.Td>
  );
}

function renderValue(value: ReactNode) {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '—';
  }
  return value || '—';
}
